import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, User as UserIcon, LogOut, Shield, Save, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  TextInput,
  FieldLabel,
  PrimaryButton,
  SecondaryButton,
  Overline,
  SectionCard,
} from "@/components/ui-kit";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout, setUser } = useAuth();
  const { lang } = useLang();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  const [name, setName] = useState(user?.name || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  async function saveProfile(e) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(lang === "es" ? "El nombre no puede estar vacío" : "Name cannot be empty");
      return;
    }
    setSavingProfile(true);
    try {
      const res = await api.patch("/auth/me", { name: name.trim() });
      setUser?.(res.data);
      toast.success(lang === "es" ? "Perfil actualizado" : "Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error(lang === "es" ? "Mínimo 8 caracteres" : "Minimum 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(lang === "es" ? "Las contraseñas no coinciden" : "Passwords don't match");
      return;
    }
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(lang === "es" ? "Contraseña actualizada" : "Password updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setChangingPassword(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#E8E6E1] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => nav("/")}
            className="inline-flex items-center gap-2 text-sm text-[#5C5B5A] hover:text-[#1A1A1A] transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {lang === "es" ? "Volver al panel" : "Back to dashboard"}
          </button>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={logout} className="text-[#5C5B5A] hover:text-[#1A1A1A] p-1" aria-label="logout">
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div>
          <Overline>{lang === "es" ? "Tu cuenta" : "Your account"}</Overline>
          <h1
            className="mt-2 text-4xl sm:text-5xl text-[#1A1A1A] tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            data-testid="settings-title"
          >
            {lang === "es" ? "Ajustes" : "Settings"}
          </h1>
          <p className="mt-2 text-sm text-[#5C5B5A]">
            {user?.email}
            {isAdmin && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#B89154]/40 bg-[#B89154]/10 text-[#8a6a37]">
                <Shield className="h-3 w-3" strokeWidth={1.5} />
                {lang === "es" ? "Administrador" : "Admin"}
              </span>
            )}
          </p>
        </div>

        {/* Profile */}
        <SectionCard
          title={lang === "es" ? "Perfil" : "Profile"}
          subtitle={lang === "es" ? "Tu nombre visible en colaboraciones." : "Your display name on collaborations."}
        >
          <form onSubmit={saveProfile} className="space-y-5 max-w-lg">
            <div>
              <FieldLabel><UserIcon className="h-3.5 w-3.5 inline mr-1.5" strokeWidth={1.5} />{lang === "es" ? "Nombre" : "Name"}</FieldLabel>
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang === "es" ? "Tu nombre" : "Your name"}
                testId="settings-name-input"
              />
            </div>
            <div>
              <FieldLabel>{lang === "es" ? "Email" : "Email"}</FieldLabel>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="w-full bg-[#FBFAF7] border border-[#E8E6E1] rounded-sm px-4 py-2.5 text-base text-[#5C5B5A] cursor-not-allowed"
                data-testid="settings-email-input"
              />
              <p className="mt-1.5 text-xs text-[#8A8886]">
                {lang === "es"
                  ? "El email es único y no se puede cambiar. Si lo necesitas, crea una cuenta nueva."
                  : "Email cannot be changed. Create a new account if needed."}
              </p>
            </div>
            <PrimaryButton type="submit" disabled={savingProfile} testId="settings-save-profile-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
              {savingProfile ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Guardar nombre" : "Save name")}
            </PrimaryButton>
          </form>
        </SectionCard>

        {/* Password */}
        <SectionCard
          title={lang === "es" ? "Contraseña" : "Password"}
          subtitle={lang === "es" ? "Actualiza tu contraseña. Mínimo 8 caracteres." : "Update your password. Minimum 8 characters."}
        >
          <form onSubmit={changePassword} className="space-y-5 max-w-lg">
            <div>
              <FieldLabel><Lock className="h-3.5 w-3.5 inline mr-1.5" strokeWidth={1.5} />{lang === "es" ? "Contraseña actual" : "Current password"}</FieldLabel>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-4 py-2.5 outline-none transition-all"
                data-testid="settings-current-password"
                required
                autoComplete="current-password"
              />
            </div>
            <div>
              <FieldLabel>{lang === "es" ? "Nueva contraseña" : "New password"}</FieldLabel>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-4 py-2.5 outline-none transition-all"
                data-testid="settings-new-password"
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div>
              <FieldLabel>{lang === "es" ? "Confirmar nueva contraseña" : "Confirm new password"}</FieldLabel>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-4 py-2.5 outline-none transition-all"
                data-testid="settings-confirm-password"
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <PrimaryButton type="submit" disabled={changingPassword} testId="settings-change-password-btn">
              {changingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" strokeWidth={1.5} />}
              {changingPassword
                ? (lang === "es" ? "Cambiando…" : "Updating…")
                : (lang === "es" ? "Cambiar contraseña" : "Change password")}
            </PrimaryButton>
          </form>
        </SectionCard>

        {/* Admin shortcut */}
        {isAdmin && (
          <SectionCard
            title={lang === "es" ? "Administración" : "Administration"}
            subtitle={lang === "es" ? "Gestiona los usuarios de Atelier Business." : "Manage Atelier Business users."}
          >
            <SecondaryButton onClick={() => nav("/admin/users")} testId="open-admin-users-btn">
              <Shield className="h-4 w-4" strokeWidth={1.5} />
              {lang === "es" ? "Gestionar usuarios" : "Manage users"}
            </SecondaryButton>
          </SectionCard>
        )}
      </main>
    </div>
  );
}
